import { createProfile, IProfile } from "./profiles";
import { UserRole } from "./roles";
import { UserSource } from "./source";
import { TabuProfile } from "./TabuProfile";
import { LocaleValue } from "@eres/core/dist/LocaleValue";
import { TaqymeeProfile } from "./TaqymeeProfile";
import { RDCProfile } from "./RDCProfile";
import { EjariProfile } from "./EjariProfile";
import { SimsariProfile } from "./SimsariProfile";
import { TrakheesiProfile } from "./TrakheesiProfile";
import { SSOProfile } from "./SSOProfile";
import Actions from "@my-dld/actions";
import { SSOApi } from "@eres/ssoapi";
import { setSSOUser } from "@my-dld/utils/User";
import Cookies from "universal-cookie";
import { getAppServices } from "@10x/coreapp";

const cookies = new Cookies();

export type IdType = string | number;

const DATA: { user?: IUser } = {
	user: undefined,
};

export const SSOCallback = {
	updateUser: ( user?: IUser ) => {
		console.log( "Dummy SSO user update" );
	},
	getStoredUser: () => {
		return undefined as any;
	}
};

export interface UserNotification {
	title: LocaleValue;
	subTitle: LocaleValue;
	type: "CRITICAL" | "WARN",
	code: string,
}

export interface IUser {
	version: number;
	profiles: IProfile[];
	addProfile: ( profile: IProfile ) => void;
	uniqId: IdType;

	hasRole( role: UserRole ): boolean;

	checkRole( roleMap: number ): boolean;

	getProfile( source: UserSource ): IProfile | undefined;

	getActiveProfile(): IProfile;

	removeProfile( profile: IProfile ): IProfile;

	setActiveProfile( source: UserSource ): void;

	getUserNotification(): UserNotification[] | undefined;
}

export class User implements IUser {
	version: number;
	profiles: IProfile[];
	uniqId: IdType;
	activeProfile?: IProfile;

	constructor( version: number, uniqId: IdType, ...profiles: IProfile[] ) {
		this.version = version;
		this.profiles = profiles;
		this.uniqId = uniqId;
	}

	public hasRole( role: UserRole ): boolean {
		return this.profiles.find( profile => profile.hasRole( role ) ) != undefined;
	}

	public addProfile( profile: IProfile ) {
		if ( profile == null ) return;
		this.profiles = [ ...this.profiles, profile ];
		refreshUser( this );
	}

	public getProfile( source: UserSource ) {
		if ( this.profiles ) {
			return this.profiles.find( x => x && x.source === source );
		}
		return undefined;
	}

	public checkRole( roleMap: number ): boolean {
		if ( this.profiles ) {
			const matchedProfiles = this.profiles.filter( x => x.checkRole( roleMap ) );
			if ( matchedProfiles && matchedProfiles.length > 0 ) return true;
		}
		return false;
	}

	getActiveProfile(): IProfile {
		if ( this.activeProfile == undefined ) {
			this.activeProfile = this.profiles[ 0 ];
		}
		return this.activeProfile;
	}

	removeProfile( profile: IProfile ): IProfile {
		const p = this.profiles.find( x => x.source === profile.source );
		if ( p ) {
			this.profiles = this.profiles.filter( x => x.source !== profile.source );
			if ( this.profiles.length > 0 )
				refreshUser( this );
			else {
				logout();
				getAppServices().storeService.dispatch( {
					type: Actions.USER_LOGGED_OUT,
				} );
			}
		}
		return profile;
	}


	setActiveProfile( source: UserSource ): void {
		const p = this.profiles.find( x => x.source === source );
		if ( p )
			this.activeProfile = p;
	}

	getUserNotification(): UserNotification[] | undefined {
		try {
			const p = this.getActiveProfile();
			if ( ( p as any ).notifications ) {
				return ( p as any ).notifications;
			}
		} catch ( e ) {

		}
		return [];
	}
}

function restoreUser( savedData: any ): IUser | undefined {
	if ( savedData && savedData.profiles ) {
		let profiles = savedData.profiles.map( ( p: any ) => {
			return buildProfile( p.source, p );
		} );
		if ( profiles ) {
			const u = new User( savedData.version, savedData.uniqId, ...profiles );
			return u;
		}
	}
	return undefined;
}

export function isLoggedIn() {
	return getUser() != undefined;
}

export function hasRole( role: UserRole ): boolean {
	if ( isLoggedIn() && getUser() )
		return getUser()!.hasRole( role );
	return false;
}

export function checkRole( roleMap: number ): boolean {
	if ( isLoggedIn() && getUser() )
		return getUser()!.checkRole( roleMap );
	return false;
}

export function getUser(): IUser | undefined {
	if ( !DATA.user ) {
		let stored = SSOCallback.getStoredUser();
		if ( stored ) DATA.user = restoreUser( stored );
	}
	return DATA.user;
}

export function createUser<T extends IProfile, X>( c: { new( data: X ): T; }, version: number, data: X ): IUser {
	const p = createProfile( c, data );
	const u = new User( version, p.id, p );
	return u;
}

export function buildProfile( source: UserSource, profile: any ): IProfile | undefined {
	switch ( source ) {
		case UserSource.TABU:
			return createProfile( TabuProfile, profile );
		case UserSource.RDC:
			return createProfile( RDCProfile, profile );
			break;
		case UserSource.EJARI:
			return createProfile( EjariProfile, profile );
			break;
		case UserSource.TAQYIMEE:
			return createProfile( TaqymeeProfile, profile );
			break;
		case UserSource.DUBAI_BROKER:
			break;
		case UserSource.TRAKHEESI:
			return createProfile( TrakheesiProfile, profile );
			break;
		case UserSource.SIMSARI:
			return createProfile( SimsariProfile, profile );
			break;
		case UserSource.SSO:
			return createProfile( SSOProfile, profile );
			break;
		default:
	}
	return undefined;
}

type ProfileProvider = {
	getProfile: () => IProfile | undefined;
};

export function getUID(): IdType | undefined {
	let user = getUser();
	if ( user ) return user.uniqId;
	return undefined;
}

function runOnProfile<T, P extends IProfile>( profile: P, task: ( profile: P ) => T ): T | undefined {
	return task( profile );
}

function runOnProfiles<T, P extends IProfile>( task: ( source: UserSource, profile: P ) => T ): T | undefined {

	const Providers: ProfileProvider[] = [ TabuProfile, TaqymeeProfile, RDCProfile, EjariProfile, TrakheesiProfile, SSOProfile ];
	for ( const provider of Providers ) {
		let p: IProfile | undefined = provider.getProfile();
		if ( p ) {
			let t = task( p.source, p as P );
			if ( t ) return t;
		}
	}
	return undefined;
}

export function getParticipantID(): number | undefined {
	return runOnProfiles( ( source: UserSource, profile: IProfile ) => {
		switch ( source ) {
			case UserSource.TABU:
				return ( profile as TabuProfile ).tabuParticipantId;
				break;
			case UserSource.RDC:
				return ( profile as RDCProfile ).Identity;
				break;
			case UserSource.EJARI:
				return ( profile as EjariProfile ).ParticipantId;
				break;
			case UserSource.TAQYIMEE:
				return ( profile as TaqymeeProfile ).participantId;
				break;
			case UserSource.DUBAI_BROKER:
				break;
			case UserSource.TRAKHEESI:
				return ( profile as TrakheesiProfile ).UserObject.ParticipantId;
				break;
			default:
		}
		return undefined;
	} );
	return undefined;
}

export function getMobileNumber(): string | undefined {
	return runOnProfiles( ( source: UserSource, profile: IProfile ) => {
		switch ( source ) {
			case UserSource.TABU:
				return ( profile as TabuProfile ).phoneNumber;
				break;
			case UserSource.RDC:
				return ( profile as RDCProfile ).MobileNumber;
				break;
			case UserSource.EJARI:
				return ( profile as EjariProfile ).Mobile;
				break;
			case UserSource.TAQYIMEE:
				return ( profile as TaqymeeProfile ).phoneNumber;
				break;
			case UserSource.DUBAI_BROKER:
				break;
			case UserSource.TRAKHEESI:
				return ( profile as TrakheesiProfile ).Mobile;
				break;
			case UserSource.SSO:
				return ( profile as SSOProfile ).mobileNumber;
				break;
			default:
		}
		return undefined;
	} );
	return undefined;
}

export function getEmail(): string | undefined {
	return runOnProfiles( ( source: UserSource, profile: IProfile ) => {
		switch ( source ) {
			case UserSource.TABU:
				return ( profile as TabuProfile ).emailAddress;
				break;
			case UserSource.RDC:
				return undefined;
				break;
			case UserSource.EJARI:
				return ( profile as EjariProfile ).Email;
				break;
			case UserSource.TAQYIMEE:
				return ( profile as TaqymeeProfile ).emailAddress;
				break;
			case UserSource.DUBAI_BROKER:
				break;
			case UserSource.TRAKHEESI:
				return ( profile as TrakheesiProfile ).Email;
				break;
			case UserSource.SSO:
				return ( profile as SSOProfile ).email;
				break;
			default:
		}
		return undefined;
	} );
	return undefined;
}

export function getName(): LocaleValue | undefined {
	return runOnProfiles( ( source: UserSource, profile: IProfile ) => {
		switch ( source ) {
			case UserSource.TABU:
				const tp = profile as TabuProfile;
				return {
					en: tp.nameEn,
					ar: tp.nameAr,
				};
				break;
			case UserSource.RDC:
				const rp = profile as RDCProfile;
				return {
					en: rp.LastName ? ( rp.FirstName + " " + rp.LastName ) : rp.FirstName,
					ar: undefined,
				};
				break;
			case UserSource.EJARI:
				const ep = ( profile as EjariProfile );
				return {
					en: ep.ParticipantNameEn,
					ar: ep.ParticipantNameAr,
				};
				break;
			case UserSource.TAQYIMEE:
				const tqp = profile as TaqymeeProfile;
				return {
					en: tqp.nameEn,
					ar: tqp.nameAr,
				};
				break;
			case UserSource.DUBAI_BROKER:
				break;
			case UserSource.TRAKHEESI:
				const trp = profile as TrakheesiProfile;
				return {
					en: trp.PersonNameEn,
					ar: trp.PersonNameAr,
				};
				break;
			case UserSource.SSO:
				const ssp = profile as SSOProfile;
				return {
					en: ssp.fullName,
					ar: ssp.fullNameAr,
				};
				break;
			default:
		}
		return undefined;
	} );
	return undefined;
}

export function getEmiratesID() {
	return runOnProfiles( ( source: UserSource, profile: IProfile ) => {
		switch ( source ) {
			case UserSource.TABU:
				return ( profile as TabuProfile ).emiratesId;
				break;
			case UserSource.RDC:
				return undefined;
				break;
			case UserSource.EJARI:
				break;
			case UserSource.TAQYIMEE:
				return ( profile as TaqymeeProfile ).emiratesId;
				break;
			case UserSource.DUBAI_BROKER:
				break;
			case UserSource.TRAKHEESI:
				break;
			case UserSource.SSO:
				return ( profile as SSOProfile ).emiratesId;
			default:
		}
		return undefined;
	} );
	return undefined;
}

export function getCountryID(): number | undefined {
	return runOnProfiles( ( source: UserSource, profile: IProfile ) => {
		switch ( source ) {
			case UserSource.TABU:
				break;
			case UserSource.RDC:
				break;
			case UserSource.EJARI:
				break;
			case UserSource.TAQYIMEE:
				break;
			case UserSource.DUBAI_BROKER:
				break;
			case UserSource.TRAKHEESI:
				break;
			default:
		}
		return undefined;
	} );
	return undefined;
}

export function getPassportNumber(): string | undefined {
	return runOnProfiles( ( source: UserSource, profile: IProfile ) => {
		switch ( source ) {
			case UserSource.TABU:
				break;
			case UserSource.RDC:
				break;
			case UserSource.EJARI:
				break;
			case UserSource.TAQYIMEE:
				break;
			case UserSource.DUBAI_BROKER:
				break;
			case UserSource.TRAKHEESI:
				break;
			default:
		}
		return undefined;
	} );
	return undefined;
}

export interface ProfileUpdate {
	email?: string;
	mobile?: string;
}

export function updateProfile( update: ProfileUpdate ) {
	let user = getUser();
	if ( user ) {
		let tp = TabuProfile.getProfile();
		if ( tp ) {
			if ( update.email ) tp.emailAddress = update.email;
			if ( update.mobile ) tp.phoneNumber = update.mobile;
		}
		refreshUser( user );
	}
}

export function clearLocalData() {
	DATA.user = undefined;
}

export function logout(): Promise<any> {
	clearLocalData();
	clearInterval( data.interval );
	return SSOApi.profile.logout().catch( r => false );
}

interface IData {
	interval?: number;
}

const data: IData = {
	interval: undefined,
};

function refreshUser( user: IUser ) {
	SSOCallback.updateUser && SSOCallback.updateUser( user );
	DATA.user = undefined;
	const u = getUser();
	const sso = SSOProfile.getProfile();
	if ( sso ) {
		clearInterval( data.interval );
		data.interval = setInterval( ssoRefresh, 1000 * 60 * 2 ) as any as number;
	} else {
		clearInterval( data.interval );
	}
}

export function setUser( user: IUser ) {
	let current = getUser();
	if ( current && current.profiles && current.profiles.length > 0 ) {
		current.profiles.forEach( p => {
			let newProfile = user.profiles.find( x => x.source === p.source );
			if ( newProfile == undefined )
				user.addProfile( p );
		} );
	}
	refreshUser( user );
}

function ssoRefresh() {
	SSOApi.silentTokenRefresh( profile => {
		if ( ( profile as any ).error == null ) {
			setSSOUser( profile );
		}
	}, error => {
		const u = getUser();
		let ssoP = SSOProfile.getProfile();
		if ( u && ssoP ) {
			u.removeProfile( ssoP );
		}
	} );
}

export function isOwnerLoggedIn(): boolean {
	let user = getUser();
	if ( user ) {
		const p = user.getActiveProfile();
		return p && p.source === UserSource.TABU;
	}
	return false;
}

export function getOwnerSSOAccountId() {
	const ep = TabuProfile.getProfile();
	if ( ep ) return ep.ssoUserId;
	return 0;
}

export function getManagementActiveUserId() {
	const ep = EjariProfile.getProfile();
	if ( ep ) return ep.Identity;
	return 0;
}

export function getManagementUserTypeId() {
	const ep = EjariProfile.getProfile();
	if ( ep ) return ep.UserTypeId;
	return 0;
}

export function getEjariActiveUserId() {
	if ( isOwnerLoggedIn() ) {
		const x = getOwnerSSOAccountId();
		if ( x != undefined ) return x;
		else return 0;
	} else {
		return getManagementActiveUserId();
	}
}

export function getEjariUserType() {
	return isOwnerLoggedIn() ? 4 : getManagementUserTypeId();
}

export function getEjariParticipantId() {
	return isOwnerLoggedIn() ? getParticipantID() : 0;
}

export function getEjariManagementId() {
	return isOwnerLoggedIn() ? 0 : getParticipantID();
}

export function getAskMalikParticipantID(): string | undefined {
	return runOnProfiles( ( source: UserSource, profile: IProfile ) => {
		switch ( source ) {
			case UserSource.TABU:
				return `tabu-${ ( profile as TabuProfile ).tabuParticipantId }`;
			case UserSource.RDC:
				return `rdc-${ ( profile as RDCProfile ).Identity }`;
			case UserSource.EJARI:
				return `ejari-${ ( profile as EjariProfile ).ParticipantId }`;
			case UserSource.TAQYIMEE:
				return `taqyimee-${ ( profile as TaqymeeProfile ).participantId }`;
			case UserSource.TRAKHEESI:
				return `trakheesi-${ ( profile as TrakheesiProfile ).UserObject.ParticipantId }`;
			default:
		}
		return undefined;
	} );
}
